/**
 * `KostenuebernahmeDetail` — A3 (Antoine, 13.09.2026, `docs/PLAN-VORGANGSSEITE-UMBAU.md`
 * Einheit A): die vollstaendige Darstellung EINER Kostenuebernahme (Status, Unterzeichner,
 * Betrag, Signaturzeitpunkt, Kontaktdaten, MFA-Verfahren, archiviertes PDF bzw. Signatur-Link).
 *
 * ## Warum es diese Datei gibt
 * Antoine wollte die Kostenuebernahme direkt auf der Vorgangsseite sehen statt erst nach einem
 * Sprung in die Abrechnungs-Unterseite. Die Darstellung existierte bereits — in `pages/abrechnung.tsx`,
 * Zeile 1 des Blocks „Kostenuebernahme -> Rechnung -> Zahlungseingang". Sie ein zweites Mal
 * abzuschreiben haette zwei Wahrheiten erzeugt (Migration 357 hat genau diesen Block schon einmal
 * erweitert; eine Kopie waere beim naechsten Mal stumm zurueckgeblieben). Deshalb: EINE Komponente,
 * zwei Aufrufer —
 *  - `pages/abrechnung.tsx` rendert sie in der mittleren Spalte ihrer Grid-Zeile; die Aktionen
 *    („Anfordern", „Widerrufen", Testvertrag-Haken) bleiben dort, wo sie waren — sie gehoeren zur
 *    Seite, nicht zur Darstellung (Leitregel der Informationsarchitektur: das Dashboard fasst
 *    zusammen, editiert wird auf Builder/Abrechnung).
 *  - `ui/auftrag/AuftragKennzahlen.tsx` oeffnet sie in einem Drawer, wenn die Kachel
 *    „Kostenuebernahme" geklickt wird UND tatsaechlich eine existiert. Ohne Kostenuebernahme
 *    bleibt die Kachel das, was sie war: ein Link auf die Abrechnungs-Unterseite.
 *
 * Markup und Texte sind 1:1 aus `abrechnung.tsx` uebernommen (reines Verschieben) — die
 * Bestandstests `abrechnung-kostenuebernahme-esign-felder.test.tsx`,
 * `abrechnung-kostenuebernahme-widerruf.test.tsx` und `kostenuebernahme-pdf-download.test.tsx`
 * haengen an genau diesen Texten/Elementen und pruefen die Extraktion damit mit.
 */
import { Icon } from "../components";
import { PILL_CLASSES, costAcceptanceMfaHinweis, formatDateTime, money } from "../format";
import { kostenuebernahmeStand } from "../../lib/zahlungs-kennzahlen";
import { openAuthenticatedFile } from "../../lib/files";
import { KostenuebernahmeVersionen } from "./KostenuebernahmeVersionen";

/** Antwortform von `GET /api/events/:id/cost-acceptance` (`apps/api/src/routes/cost-acceptance.ts`). */
export interface CostAcceptance {
  id: string;
  status: "draft" | "sent" | "signed" | "declined" | "withdrawn" | "signed_ungueltig";
  signUrl: string | null;
  signedPdfUrl: string | null;
  /** M-24 Nachtrag: nicht-leer <=> das eigene Archiv (signed_pdf_base64) ist gefuellt - Signal
   *  fuer den owner/admin-geschuetzten Download ueber /api/events/:id/cost-acceptance/pdf statt
   *  der (ablaufenden) Provider-URL signedPdfUrl. */
  signedPdfSha256: string | null;
  signerName: string | null;
  /**
   * Migration 357 ("alles, was von eSign zurueckkommt, sollten wir zeigen" — Antoine, 03.09.2026):
   * E-Mail/Mobilnummer/MFA-Verfahren des Signers. Alle drei koennen NULL sein — Bestandszeilen
   * (vor Migration 357 signiert) oder wenn der Provider sie nicht lieferte. Die UI blendet dann
   * aus statt zu raten (kein Platzhalter-Text).
   */
  signerEmail: string | null;
  signerMobile: string | null;
  mfaMethods: string[] | null;
  amountGrossCents: number | null;
  /** Migration 421 — Zustellzeitpunkt (Uebergang draft -> sent), s. WARTESCHLANGE Punkt 6. */
  sentAt: string | null;
  signedAt: string | null;
  createdAt: string;
  /**
   * KÜ V2 (S2, R2): der Server liefert die GÜLTIGE Kostenübernahme; wartet daneben eine neuere,
   * geänderte Fassung auf Unterschrift, steht sie hier (sonst `null`/fehlt).
   */
  offeneVersion?: { id: string; versionNo: number | null; signUrl: string | null; sentAt: string | null; amountGrossCents: number | null } | null;
}

// WP2 (Auftrags-Dashboard-Kennzahlen, 08.09.2026): Status-Text+Ton kamen vorher in
// `abrechnung.tsx` UND im Kennzahlen-Kopf des Dashboards (`ui/auftrag/AuftragKennzahlen.tsx`)
// fast identisch vor — die Wahrheit dazu liegt seither in
// `lib/zahlungs-kennzahlen.ts::kostenuebernahmeStand`, hier nur noch die Pillen-Klasse dazu.
export const caMeta = (s: string): { label: string; cls: string } => {
  const stand = kostenuebernahmeStand({ status: s });
  return { label: stand.label, cls: PILL_CLASSES[stand.tone] };
};

/** „Nicht angefordert" — zugleich der Leerzustands-Ton der Rechnungs-Zeile in `abrechnung.tsx`. */
export const NOT_REQUESTED = (() => {
  const stand = kostenuebernahmeStand(null);
  return { label: stand.label, cls: PILL_CLASSES[stand.tone] };
})();

export function KostenuebernahmeDetail({
  eventId,
  costAcceptance,
}: {
  eventId: string;
  /** `null`/`undefined` = noch nie angefordert (`GET /api/events/:id/cost-acceptance` -> null). */
  costAcceptance: CostAcceptance | null | undefined;
}) {
  const ca = costAcceptance ?? null;
  // Migration 357: nur wenn der Provider tatsaechlich ein bekanntes MFA-Verfahren lieferte -
  // sonst null (keine Anzeige, kein Platzhalter-Text, s. costAcceptanceMfaHinweis-Doku).
  const mfaHinweis = costAcceptanceMfaHinweis(ca?.mfaMethods);

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <p className="font-body-md text-body-md font-semibold text-on-background dark:text-inverse-on-surface">Kostenübernahme</p>
        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 font-label-md text-[11px] ${ca ? caMeta(ca.status).cls : NOT_REQUESTED.cls}`}>
          {ca ? caMeta(ca.status).label : NOT_REQUESTED.label}
        </span>
      </div>
      <p className="mt-1 font-body-sm text-[11.5px] text-secondary dark:text-secondary-fixed-dim">
        Verbindliche eSign-Zusage für Firmenkunden — Bedingungen aus den Einstellungen.
      </p>
      {ca?.signerName && (
        <p className="mt-1 font-body-sm text-[11.5px] text-secondary dark:text-secondary-fixed-dim">
          {ca.signerName}
          {/* null = ausdruecklich "kein fester Endbetrag" (A-la-carte, noFixedAmount) -
              routes/cost-acceptance.ts speichert null nur noch fuer genau diesen Fall. */}
          {ca.amountGrossCents != null ? ` · ${money(ca.amountGrossCents)}` : " · A-la-carte, kein fester Endbetrag"}
        </p>
      )}
      {/* Migration 421 — WARTESCHLANGE Punkt 6 ("was hat der Gast bereits zugeschickt
          bekommen"): Zustellzeitpunkt, zeitlich VOR dem Signieren, nur wenn vorhanden
          (Bestandsfaelle vor der Migration haben kein sentAt - kein Platzhalter). */}
      {ca?.sentAt && (
        <p className="mt-0.5 font-body-sm text-[11px] text-secondary/80 dark:text-secondary-fixed-dim/80">
          Zugestellt am {formatDateTime(ca.sentAt)}
        </p>
      )}
      {/* Migration 357 ("alles, was von eSign zurueckkommt, sollten wir zeigen" — Antoine,
          03.09.2026): permanenter Rechtsnachweis, kein Zeitfenster. Jede Zeile nur, wenn
          der Wert tatsaechlich vorliegt — kein Platzhalter, kein Raten. */}
      {ca?.signedAt && (
        <p className="mt-0.5 font-body-sm text-[11px] text-secondary/80 dark:text-secondary-fixed-dim/80">
          Signiert am {formatDateTime(ca.signedAt)}
        </p>
      )}
      {ca?.signerEmail && <p className="mt-0.5 font-body-sm text-[11px] text-secondary/80 dark:text-secondary-fixed-dim/80">{ca.signerEmail}</p>}
      {ca?.signerMobile && <p className="mt-0.5 font-body-sm text-[11px] text-secondary/80 dark:text-secondary-fixed-dim/80">{ca.signerMobile}</p>}
      {mfaHinweis && <p className="mt-0.5 font-body-sm text-[11px] text-secondary/80 dark:text-secondary-fixed-dim/80">{mfaHinweis}</p>}
      {ca?.status === "signed" && ca.signedPdfSha256 ? (
        // M-24 Nachtrag: eigenes Archiv statt der (ablaufenden) Provider-URL - owner/admin-
        // geschuetzter Download ueber Blob-URL (lib/files.ts), weil ein simples <a href>
        // den Auth-Header nicht mitschicken koennte.
        <button
          type="button"
          onClick={() => void openAuthenticatedFile(`/api/events/${eventId}/cost-acceptance/pdf`)}
          className="mt-1 inline-flex items-center gap-1.5 font-label-md text-label-md text-primary hover:underline"
        >
          <Icon name="picture_as_pdf" className="text-[16px]" /> Signierte Kostenübernahme (PDF)
        </button>
      ) : ca?.status === "signed" && ca.signedPdfUrl ? (
        // Archivierung (best-effort) noch nicht durch - Fallback auf die Provider-URL.
        <a href={ca.signedPdfUrl} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1.5 font-label-md text-label-md text-primary hover:underline">
          <Icon name="picture_as_pdf" className="text-[16px]" /> Signierte Kostenübernahme (PDF)
        </a>
      ) : ca?.signUrl ? (
        <a href={ca.signUrl} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1.5 font-label-md text-label-md text-primary hover:underline">
          <Icon name="open_in_new" className="text-[16px]" /> Signatur-Link öffnen
        </a>
      ) : null}
      {ca?.offeneVersion && (
        <p className="mt-1 font-body-sm text-[11.5px] text-secondary dark:text-[#b8ae9e]" data-testid="kue-offene-version-hinweis">
          Geänderte Fassung V{ca.offeneVersion.versionNo ?? "?"} wartet auf Unterschrift
          {ca.offeneVersion.sentAt ? ` (zugestellt am ${formatDateTime(ca.offeneVersion.sentAt)})` : ""} — bis dahin gilt diese.
        </p>
      )}
      {/* KÜ V2 (S1): alle Versionen, sobald es mehr als eine gibt — V1 bleibt sichtbar. */}
      <KostenuebernahmeVersionen eventId={eventId} />
    </>
  );
}
